import { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { propertiesApi } from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '@/components/ui/table';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Checkbox } from '@/components/ui/checkbox';
import { PlusCircle, FileDown, Trash2, Pencil, Building, TrendingUp, BarChart3, Users, Bookmark, ArrowUpDown, ArrowUp, ArrowDown, CheckSquare } from 'lucide-react';
import { toast } from 'sonner';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell } from 'recharts';

const API_URL = process.env.NODE_ENV === 'production' ? '' : (process.env.REACT_APP_BACKEND_URL || '');

function demandBadge(pct, ratingColor) {
  const colorMap = {
    emerald: 'bg-emerald-50 text-emerald-700 border-emerald-200 hover:bg-emerald-100',
    teal: 'bg-teal-50 text-teal-700 border-teal-200 hover:bg-teal-100',
    blue: 'bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100',
    slate: 'bg-slate-50 text-slate-700 border-slate-200 hover:bg-slate-100',
    yellow: 'bg-yellow-50 text-yellow-700 border-yellow-200 hover:bg-yellow-100',
    amber: 'bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100',
    orange: 'bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100',
    red: 'bg-red-50 text-red-700 border-red-200 hover:bg-red-100',
  };
  const cls = colorMap[ratingColor] || (pct <= 85 ? colorMap.emerald : pct <= 100 ? colorMap.blue : pct <= 130 ? colorMap.amber : colorMap.red);
  return <Badge className={cls}>{pct}%</Badge>;
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  try {
    const d = new Date(dateStr);
    if (isNaN(d.getTime())) return '-';
    return d.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' ' + d.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
  } catch { return '-'; }
}

const PIE_COLORS = ['#10B981', '#F59E0B', '#EF4444', '#6366F1'];

export default function AdminDashboard() {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('alle');
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [selected, setSelected] = useState(new Set());
  const navigate = useNavigate();

  const fetchProperties = async () => {
    try {
      const res = await propertiesApi.list();
      setProperties(res.data.properties || res.data || []);
    } catch (e) { toast.error('Fehler beim Laden'); }
    finally { setLoading(false); }
  };
  useEffect(() => { fetchProperties(); }, []);

  const filtered = useMemo(() => {
    if (filter === 'bestand') return properties.filter(p => p.is_portfolio_property);
    if (filter === 'interessenten') return properties.filter(p => !p.is_portfolio_property && p.is_public_submission);
    if (filter === 'admin') return properties.filter(p => !p.is_public_submission);
    return properties;
  }, [properties, filter]);

  const sorted = useMemo(() => {
    const list = [...filtered];
    list.sort((a, b) => {
      let va = a[sortBy], vb = b[sortBy];
      if (sortBy === 'name') { va = (va || '').toLowerCase(); vb = (vb || '').toLowerCase(); }
      if (sortBy === 'total_price') { va = ((a.custom_fee_override || a.recommended_fee || 0)) * (a.units || 0); vb = ((b.custom_fee_override || b.recommended_fee || 0)) * (b.units || 0); }
      if (va == null) va = sortDir === 'asc' ? Infinity : -Infinity;
      if (vb == null) vb = sortDir === 'asc' ? Infinity : -Infinity;
      if (typeof va === 'string') return sortDir === 'asc' ? va.localeCompare(vb) : vb.localeCompare(va);
      return sortDir === 'asc' ? va - vb : vb - va;
    });
    return list;
  }, [filtered, sortBy, sortDir]);

  const toggleSort = (col) => {
    if (sortBy === col) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortBy(col); setSortDir(col === 'name' ? 'asc' : 'desc'); }
  };

  const SortIcon = ({ col }) => {
    if (sortBy !== col) return <ArrowUpDown className="h-3 w-3 ml-1 opacity-30" />;
    return sortDir === 'asc' ? <ArrowUp className="h-3 w-3 ml-1 text-[#1BA1E4]" /> : <ArrowDown className="h-3 w-3 ml-1 text-[#1BA1E4]" />;
  };

  const handleDelete = async (id, name) => {
    if (!confirm(`"${name || 'Analyse'}" wirklich löschen?`)) return;
    try { await propertiesApi.delete(id); toast.success('Gelöscht'); fetchProperties(); selected.delete(id); setSelected(new Set(selected)); }
    catch { toast.error('Fehler'); }
  };

  const handleBulkDelete = async () => {
    if (selected.size === 0) return;
    if (!confirm(`${selected.size} Objekt(e) wirklich löschen?`)) return;
    let ok = 0;
    for (const id of selected) {
      try { await propertiesApi.delete(id); ok++; } catch {}
    }
    toast.success(`${ok} Objekt(e) gelöscht`);
    setSelected(new Set());
    fetchProperties();
  };

  const handlePdf = async (id, name) => {
    toast.loading('PDF wird erstellt...', { duration: 5000 });
    const token = localStorage.getItem('token');
    window.open(`${API_URL}/api/properties/${id}/pdf?token=${token}`, '_blank');
  };

  const handleCustomerPdf = async (id, name) => {
    toast.loading('Kunden-PDF wird erstellt...', { duration: 5000 });
    const token = localStorage.getItem('token');
    window.open(`${API_URL}/api/properties/${id}/pdf/customer?token=${token}`, '_blank');
  };

  const handleOfferPdf = async (id, name) => {
    toast.loading('Vorabangebot wird erstellt...', { duration: 5000 });
    const token = localStorage.getItem('token');
    window.open(`${API_URL}/api/properties/${id}/pdf/offer?token=${token}`, '_blank');
  };

  const handleBulkPdf = async () => {
    if (selected.size === 0) return;
    const toastId = toast.loading(`${selected.size} PDF(s) werden erstellt...`);
    for (const id of selected) {
      const p = properties.find(x => x.id === id);
      await handlePdf(id, p?.name);
    }
    toast.success('Alle PDFs erstellt', { id: toastId });
  };

  const toggleAll = () => {
    if (selected.size === sorted.length) setSelected(new Set());
    else setSelected(new Set(sorted.map(p => p.id)));
  };

  const toggleOne = (id) => {
    const s = new Set(selected);
    s.has(id) ? s.delete(id) : s.add(id);
    setSelected(s);
  };

  const stats = useMemo(() => {
    const f = filtered;
    return {
      total: f.length,
      units: f.reduce((s, p) => s + (p.units || 0), 0),
      avgDemand: f.length ? Math.round(f.reduce((s, p) => s + (p.demand_percentage || 0), 0) / f.length) : 0,
      portfolio: f.filter(p => p.is_portfolio_property).length,
    };
  }, [filtered]);

  const demandDistribution = useMemo(() => {
    const buckets = { '<75%': 0, '100%': 0, '150%': 0, '200%': 0 };
    filtered.forEach(p => {
      const d = p.demand_percentage || 0;
      if (d < 75) buckets['<75%']++;
      else if (d <= 100) buckets['100%']++;
      else if (d <= 150) buckets['150%']++;
      else buckets['200%']++;
    });
    return Object.entries(buckets).map(([name, value]) => ({ name, value }));
  }, [filtered]);

  const avgScores = useMemo(() => {
    const f = filtered.filter(p => p.total_score != null);
    if (!f.length) return [];
    return [
      { name: 'Komplexität', score: Math.round(f.reduce((s, p) => s + (p.building_complexity_score || 0), 0) / f.length) },
      { name: 'Zustand', score: Math.round(f.reduce((s, p) => s + (p.building_condition_score || 0), 0) / f.length) },
      { name: 'Historie', score: Math.round(f.reduce((s, p) => s + (p.history_score || 0), 0) / f.length) },
    ];
  }, [filtered]);

  return (
    <div className="space-y-6" data-testid="admin-dashboard">
      <div>
        <h1 className="text-2xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-sm text-muted-foreground">Übersicht aller WEG-Analysen</p>
      </div>

      <Tabs value={filter} onValueChange={v => { setFilter(v); setSelected(new Set()); }}>
        <TabsList>
          <TabsTrigger value="alle">Alle ({properties.length})</TabsTrigger>
          <TabsTrigger value="bestand" className="gap-1.5"><Bookmark className="h-3 w-3" /> Bestand ({properties.filter(p => p.is_portfolio_property).length})</TabsTrigger>
          <TabsTrigger value="interessenten">Interessenten ({properties.filter(p => !p.is_portfolio_property && p.is_public_submission).length})</TabsTrigger>
          <TabsTrigger value="admin">Admin ({properties.filter(p => !p.is_public_submission).length})</TabsTrigger>
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {[
          { label: 'Objekte', value: stats.total, icon: Building },
          { label: 'Einheiten', value: stats.units, icon: Users },
          { label: 'Ø Bedarf', value: `${stats.avgDemand}%`, icon: BarChart3 },
          { label: 'Bestandsobjekte', value: stats.portfolio, icon: Bookmark },
        ].map(s => (
          <Card key={s.label}>
            <CardContent className="pt-6 pb-4 px-5">
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-medium text-muted-foreground">{s.label}</p>
                <s.icon className="h-4 w-4 text-muted-foreground/50" />
              </div>
              <p className="text-2xl font-bold tabular-nums">{s.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Bedarfsverteilung</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie data={demandDistribution} cx="50%" cy="50%" outerRadius={80} dataKey="value" label={({ name, value }) => `${name}: ${value}`}>
                  {demandDistribution.map((_, i) => <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v} Objekte`, n]} />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold">Durchschnittliche Scores</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={avgScores}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 12 }} />
                <YAxis domain={[0, 100]} tick={{ fontSize: 12 }} />
                <Bar dataKey="score" fill="#1BA1E4" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-semibold">Objekte ({sorted.length})</CardTitle>
            {selected.size > 0 && (
              <div className="flex items-center gap-2" data-testid="bulk-actions">
                <span className="text-xs text-muted-foreground">{selected.size} ausgewählt</span>
                <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs" onClick={handleBulkPdf} data-testid="bulk-pdf-btn">
                  <FileDown className="h-3 w-3" /> PDF Export
                </Button>
                <Button variant="outline" size="sm" className="h-7 gap-1.5 text-xs text-destructive hover:text-destructive" onClick={handleBulkDelete} data-testid="bulk-delete-btn">
                  <Trash2 className="h-3 w-3" /> Löschen
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-12 text-muted-foreground text-sm">Laden...</div>
          ) : sorted.length === 0 ? (
            <div className="text-center py-12">
              <Building className="h-10 w-10 text-muted-foreground/40 mx-auto mb-3" />
              <p className="text-sm text-muted-foreground">Keine Objekte gefunden.</p>
              <Link to="/admin/analyse/neu"><Button variant="outline" className="mt-4 gap-2"><PlusCircle className="h-4 w-4" /> Erste Analyse</Button></Link>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead className="w-10">
                      <Checkbox checked={selected.size === sorted.length && sorted.length > 0} onCheckedChange={toggleAll} data-testid="select-all-checkbox" />
                    </TableHead>
                    <TableHead className="cursor-pointer select-none" onClick={() => toggleSort('name')} data-testid="sort-name">
                      <span className="flex items-center">Bezeichnung<SortIcon col="name" /></span>
                    </TableHead>
                    <TableHead className="text-center cursor-pointer select-none" onClick={() => toggleSort('created_at')} data-testid="sort-date">
                      <span className="flex items-center justify-center">Datum<SortIcon col="created_at" /></span>
                    </TableHead>
                    <TableHead className="text-center cursor-pointer select-none" onClick={() => toggleSort('units')} data-testid="sort-units">
                      <span className="flex items-center justify-center">Einheiten<SortIcon col="units" /></span>
                    </TableHead>
                    <TableHead className="text-center cursor-pointer select-none" onClick={() => toggleSort('demand_percentage')} data-testid="sort-demand">
                      <span className="flex items-center justify-center">Bedarf<SortIcon col="demand_percentage" /></span>
                    </TableHead>
                    <TableHead className="text-center cursor-pointer select-none" onClick={() => toggleSort('total_score')} data-testid="sort-score">
                      <span className="flex items-center justify-center">Score<SortIcon col="total_score" /></span>
                    </TableHead>
                    <TableHead className="text-center cursor-pointer select-none" onClick={() => toggleSort('recommended_fee')} data-testid="sort-fee">
                      <span className="flex items-center justify-center">EUR/Einheit<SortIcon col="recommended_fee" /></span>
                    </TableHead>
                    <TableHead className="text-center cursor-pointer select-none" onClick={() => toggleSort('total_price')} data-testid="sort-total">
                      <span className="flex items-center justify-center">Gesamt netto<SortIcon col="total_price" /></span>
                    </TableHead>
                    <TableHead className="text-center">Erstellt von</TableHead>
                    <TableHead className="text-right">Aktionen</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {sorted.map(p => {
                    const baseFee = p.recommended_fee || p.base_fee_per_unit || 0;
                    const effectiveFee = p.custom_fee_override || baseFee;
                    const hasOverride = p.custom_fee_override != null && p.custom_fee_override !== baseFee;
                    const totalNet = effectiveFee * (p.units || 0);
                    return (
                      <TableRow key={p.id} className="group" data-testid={`row-${p.id}`}>
                        <TableCell>
                          <Checkbox checked={selected.has(p.id)} onCheckedChange={() => toggleOne(p.id)} data-testid={`select-${p.id}`} />
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            {p.is_portfolio_property && <Bookmark className="h-3.5 w-3.5 text-[#1BA1E4] shrink-0" />}
                            <div>
                              <button onClick={() => navigate(`/admin/analyse/${p.id}`)} className="text-sm font-medium hover:text-[#1BA1E4] transition-colors duration-150 text-left" data-testid={`prop-link-${p.id}`}>
                                {p.name || 'Ohne Bezeichnung'}
                              </button>
                              <p className="text-xs text-muted-foreground">{p.address ? `${p.address}, ${p.city}` : ''}</p>
                            </div>
                          </div>
                        </TableCell>
                        <TableCell className="text-center text-xs text-muted-foreground tabular-nums whitespace-nowrap">{formatDate(p.created_at)}</TableCell>
                        <TableCell className="text-center tabular-nums">{p.units}</TableCell>
                        <TableCell className="text-center">{demandBadge(p.demand_percentage, p.rating_color)}</TableCell>
                        <TableCell className="text-center tabular-nums text-sm">{p.total_score?.toFixed(1)}</TableCell>
                        <TableCell className="text-center tabular-nums text-sm">
                          {hasOverride ? (
                            <div>
                              <span className="font-semibold text-blue-700">{effectiveFee} €</span>
                              <span className="block text-[10px] text-muted-foreground line-through">{baseFee} €</span>
                            </div>
                          ) : (
                            effectiveFee ? `${effectiveFee} €` : '-'
                          )}
                        </TableCell>
                        <TableCell className="text-center tabular-nums text-sm font-medium">{totalNet ? `${Math.round(totalNet).toLocaleString('de-DE')} €` : '-'}</TableCell>
                        <TableCell className="text-center">
                          <span className="text-xs text-muted-foreground">{p.created_by_name || (p.is_public_submission ? (p.contact_name || p.contact_email || 'Öffentlich') : 'Admin')}</span>
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex items-center justify-end gap-1 opacity-50 group-hover:opacity-100 transition-opacity">
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => navigate(`/admin/analyse/${p.id}`)} data-testid={`edit-btn-${p.id}`} title="Bearbeiten"><Pencil className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0" onClick={() => handlePdf(p.id, p.name)} data-testid={`pdf-btn-${p.id}`} title="PDF (Vollständig)"><FileDown className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-blue-600" onClick={() => handleCustomerPdf(p.id, p.name)} data-testid={`customer-pdf-btn-${p.id}`} title="PDF (Kunde)"><FileDown className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 text-amber-600" onClick={() => handleOfferPdf(p.id, p.name)} data-testid={`offer-pdf-btn-${p.id}`} title="Vorabangebot PDF"><FileDown className="h-3.5 w-3.5" /></Button>
                            <Button variant="ghost" size="sm" className="h-8 w-8 p-0 hover:text-destructive" onClick={() => handleDelete(p.id, p.name)} data-testid={`delete-btn-${p.id}`} title="Löschen"><Trash2 className="h-3.5 w-3.5" /></Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
